import * as React from 'react'
import { DatePicker } from '@progress/kendo-react-dateinputs'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import { GridColumnMenuFilter, GRID_COL_INDEX_ATTRIBUTE } from '@progress/kendo-react-grid'
import { TREELIST_COL_INDEX_ATTRIBUTE } from '@progress/kendo-react-treelist'
import { useTableKeyboardNavigation, TABLE_PREVENT_SELECTION_ELEMENT } from '@progress/kendo-react-data-tools'
import { classNames, getNestedValue } from '@progress/kendo-react-common'
import { accentUtils, format, insyteDate } from '../../services/HelperService'

// Kendo Grid / TreeList / column-menu building blocks for InsyteDate ("yyyy-MM-dd") fields.
//
// They mirror Kendo's own TreeListDateEditor, GridEditCell(editor="date") and
// GridColumnMenuFilterCell(date) but the value that flows in and out of the data item and the
// filter descriptor is ALWAYS the "yyyy-MM-dd" string, never a JS Date, so nothing downstream
// (product-model JSON, PartImportItem, Breeze predicates, the adhocFilter header) ever sees an
// ISO date-time.

const pickerValue = v => insyteDate.toJsDate(v);

// ---------------------------------------------------------------------------------------------
// TreeList
// ---------------------------------------------------------------------------------------------

/** TreeList `editCell` for an InsyteDate column. Emits { dataItem, level, field, value: "yyyy-MM-dd" | null }. */
export const InsyteDateTreeListEditor = (props) => {

    const { onChange: onChangeProp, dataItem, level, field } = props;

    const onChange = React.useCallback(e => {
        if (onChangeProp) {
            onChangeProp({
                dataItem: dataItem,
                level: level,
                field: field,
                syntheticEvent: e.syntheticEvent,
                value: insyteDate.normalize(e.value)
            });
        }
    }, [onChangeProp, dataItem, level, field]);

    const navigationAttributes = useTableKeyboardNavigation(props.id);

    const className = classNames({ 'k-selected': props.isSelected }, props.className);

    const td = (
        <td
            style={props.style}
            className={className}
            aria-colindex={props.ariaColumnIndex}
            aria-selected={props.isSelected}
            role="gridcell"
            {...navigationAttributes}
            {...{ [TREELIST_COL_INDEX_ATTRIBUTE]: props.colIndex }}
        >
            <DatePicker
                width="100%"
                format="d"
                value={pickerValue(getNestedValue(props.field, props.dataItem))}
                onChange={onChange}
            />
        </td>
    );

    return props.render ? props.render.call(undefined, td, props) : td;
};
InsyteDateTreeListEditor.displayName = 'InsyteDateTreeListEditor';

/** TreeList display `cell` for an InsyteDate column (localised, no zone conversion). */
export const InsyteDateTreeListCell = (props) => {

    const navigationAttributes = useTableKeyboardNavigation(props.id);

    const value = getNestedValue(props.field, props.dataItem);

    const td = (
        <td
            style={props.style}
            className={classNames('k-table-td', props.className, { 'k-selected': props.isSelected })}
            colSpan={props.colSpan}
            aria-colindex={props.ariaColumnIndex}
            aria-selected={props.isSelected}
            role="gridcell"
            {...navigationAttributes}
            {...{ [TREELIST_COL_INDEX_ATTRIBUTE]: props.colIndex }}
        >
            {format.formatInsyteDate(value)}
        </td>
    );

    return props.render ? props.render.call(undefined, td, props) : td;
};
InsyteDateTreeListCell.displayName = 'InsyteDateTreeListCell';

// ---------------------------------------------------------------------------------------------
// Grid
// ---------------------------------------------------------------------------------------------

/**
 * Grid `cell` for an InsyteDate column. Shows the localised date, or a picker while the row is in
 * edit mode (dataItem[editField] / dataItem.inEdit). Emits { dataItem, dataIndex, field, value: "yyyy-MM-dd" | null }.
 */
export const InsyteDateGridCell = (props) => {

    const navigationAttributes = useTableKeyboardNavigation(props.id);

    const value = getNestedValue(props.field, props.dataItem);

    const { onChange: onChangeProp, dataItem, dataIndex, field } = props;

    const editField = props.editField ?? 'inEdit';
    const inEdit = !accentUtils.isNull(dataItem) && dataItem[editField] === true;

    const onChange = React.useCallback(e => {
        if (onChangeProp) {
            onChangeProp({
                dataItem: dataItem,
                dataIndex: dataIndex,
                field: field,
                syntheticEvent: e.syntheticEvent,
                value: insyteDate.normalize(e.value)
            });
        }
    }, [onChangeProp, dataItem, dataIndex, field]);

    const tdProps = {
        colSpan: props.colSpan,
        style: props.style,
        className: classNames('k-table-td', { 'k-grid-edit-cell': inEdit, 'k-selected': props.isSelected }, props.className),
        'aria-colindex': props.ariaColumnIndex,
        'aria-selected': props.isSelected,
        role: 'gridcell',
        [GRID_COL_INDEX_ATTRIBUTE]: props.columnIndex,
        ...navigationAttributes
    };

    const td = inEdit
        ? (
            <td {...tdProps}>
                <DatePicker
                    format="d"
                    value={pickerValue(value)}
                    onChange={onChange}
                    {...{ [TABLE_PREVENT_SELECTION_ELEMENT]: true }}
                />
            </td>
        )
        : (
            <td {...tdProps}>
                {format.formatInsyteDate(value)}
            </td>
        );

    return props.render ? props.render.call(undefined, td, props) : td;
};
InsyteDateGridCell.displayName = 'InsyteDateGridCell';

// ---------------------------------------------------------------------------------------------
// Column menu filter
// ---------------------------------------------------------------------------------------------

/** Kendo's date operator set, registered under our own "insytedate" filter type. */
export const insyteDateFilterOperators = [
    { text: 'grid.filterEqOperator', operator: 'eq' },
    { text: 'grid.filterNotEqOperator', operator: 'neq' },
    { text: 'grid.filterAfterOrEqualOperator', operator: 'gte' },
    { text: 'grid.filterAfterOperator', operator: 'gt' },
    { text: 'grid.filterBeforeOperator', operator: 'lt' },
    { text: 'grid.filterBeforeOrEqualOperator', operator: 'lte' },
    { text: 'grid.filterIsNullOperator', operator: 'isnull' },
    { text: 'grid.filterIsNotNullOperator', operator: 'isnotnull' }
];

const isUnaryOperator = op => op === 'isnull' || op === 'isnotnull' || op === 'isempty' || op === 'isnotempty';

/** One operator + date row of the filter menu; `props` are Kendo's firstFilterProps / secondFilterProps. */
const InsyteDateFilterRow = (props) => {

    const { value, operator, operators, onChange } = props;

    const currentOperator = operators.find(o => o.operator === operator) || null;

    const operatorChange = e => {
        const nextOperator = e.target.value.operator;
        let nextValue = value;
        if (isUnaryOperator(nextOperator)) {
            nextValue = null;
        } else if (value === null && !isUnaryOperator(nextOperator)) {
            nextValue = undefined;
        }
        onChange({ value: nextValue, operator: nextOperator, syntheticEvent: e.syntheticEvent });
    };

    const dateChange = e => {
        const nextValue = insyteDate.normalize(e.value);
        let nextOperator = operator;
        if (!nextOperator || isUnaryOperator(nextOperator)) {
            nextOperator = operators[0].operator;
        }
        // Kendo semantics: clearing the date with the default operator resets the row.
        if (nextValue === null && nextOperator === operators[0].operator) {
            nextOperator = '';
        }
        onChange({ value: nextValue ?? undefined, operator: nextOperator, syntheticEvent: e.syntheticEvent });
    };

    return (
        <React.Fragment>
            <DropDownList value={currentOperator} onChange={operatorChange} data={operators} textField="text" />
            <DatePicker
                format="d"
                value={pickerValue(value)}
                onChange={dateChange}
                disabled={isUnaryOperator(operator)}
            />
        </React.Fragment>
    );
};

/** `filterUI` for GridColumnMenuFilter: same layout as Kendo's GridColumnMenuFilterUI, "yyyy-MM-dd" values. */
export const InsyteDateFilterUI = (props) => {

    const { firstFilterProps, secondFilterProps, hideSecondFilter, logicData, logicValue, onLogicChange } = props;

    return (
        <React.Fragment>
            <InsyteDateFilterRow {...firstFilterProps} />
            {!hideSecondFilter && (
                <React.Fragment>
                    <DropDownList data={logicData} value={logicValue} onChange={onLogicChange} className="k-filter-and" textField="text" />
                    <InsyteDateFilterRow {...secondFilterProps} />
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

/** Ensures an "insytedate" operator set exists in whatever the Grid passed down (so column filter="insytedate" resolves). */
export const withInsyteDateOperators = (filterOperators) => ({
    ...(filterOperators || {}),
    insytedate: filterOperators?.insytedate ?? insyteDateFilterOperators
});

/**
 * Coerces the values of any descriptors on the given InsyteDate fields inside a Kendo
 * CompositeFilterDescriptor to "yyyy-MM-dd" strings. Guards persisted grid filters that were saved
 * as JS Dates / ISO date-times before these columns became calendar-only, which the strict
 * server-side InsyteDate converter would otherwise reject.
 */
export const normalizeInsyteDateFilterValues = (filter, insyteDateFields) => {

    if (accentUtils.isNull(filter) || (insyteDateFields?.length ?? 0) === 0) return filter;

    if (Array.isArray(filter.filters)) {
        return { ...filter, filters: filter.filters.map(f => normalizeInsyteDateFilterValues(f, insyteDateFields)) };
    }

    if (!accentUtils.isNull(filter.field) && insyteDateFields.indexOf(filter.field) !== -1 && !accentUtils.isNull(filter.value)) {
        return { ...filter, value: insyteDate.normalize(filter.value) };
    }

    return filter;
};

/** Drop-in for GridColumnMenuFilter on an InsyteDate column. */
export const InsyteDateColumnMenuFilter = (props) => (
    <GridColumnMenuFilter
        {...props}
        filterOperators={withInsyteDateOperators(props.filterOperators)}
        filterUI={InsyteDateFilterUI}
    />
);
